/**
 * Mock data for local development before the API is wired up.
 *
 * Replace usage of these with React Query hooks that hit the API in Phase 2.
 * Keeping this in a single file makes the swap mechanical: search for
 * imports of `mockData` and replace with `useChallenges()` etc.
 */
import type { Challenge, NotificationDetail, PublicUser, Submission } from '@jemmi/schemas';

const NOW = Date.now();
const hoursFromNow = (h: number) => new Date(NOW + h * 3600_000).toISOString();
const hoursAgo = (h: number) => new Date(NOW - h * 3600_000).toISOString();

export const mockUsers: Record<string, PublicUser> = {
  u1: { id: 'u1', displayName: 'Mina K.', avatarUrl: null },
  u2: { id: 'u2', displayName: 'Daniel R.', avatarUrl: null },
  u3: { id: 'u3', displayName: 'Alex P.', avatarUrl: null },
  u4: { id: 'u4', displayName: 'Soo-jin L.', avatarUrl: null },
  u5: { id: 'u5', displayName: 'Tom W.', avatarUrl: null },
  u6: { id: 'u6', displayName: 'Jamie L.', avatarUrl: null },
  u7: { id: 'u7', displayName: 'Priya S.', avatarUrl: null },
  u8: { id: 'u8', displayName: 'Marcus J.', avatarUrl: null },
  admin1: { id: 'admin1', displayName: 'Jemmi Admin', avatarUrl: null },
};

export const mockChallenges: Challenge[] = [
  {
    id: 'c1',
    title: 'Best sunrise photo this week',
    brief: 'Capture the most beautiful sunrise — natural light only, no filters.',
    category: 'Photography',
    creatorId: 'u1',
    creator: mockUsers.u1!,
    createdAt: hoursAgo(48),
    endsAt: hoursFromNow(18),
    submissionCount: 47,
    jemCount: 312,
    hasVoted: false,
  },
  {
    id: 'c2',
    title: 'Cook something using only 5 ingredients',
    brief: 'Show us what you made and the ingredients on the side.',
    category: 'Cooking',
    creatorId: 'u2',
    creator: mockUsers.u2!,
    createdAt: hoursAgo(24),
    endsAt: hoursFromNow(46),
    submissionCount: 23,
    jemCount: 156,
    hasVoted: true,
  },
  {
    id: 'c3',
    title: 'Cleanest project car under $5k',
    brief: 'Show off your weekend build. One photo, exterior shot.',
    category: 'Cars',
    creatorId: 'u3',
    creator: mockUsers.u3!,
    createdAt: hoursAgo(72),
    endsAt: hoursFromNow(4),
    submissionCount: 89,
    jemCount: 521,
    hasVoted: false,
  },
  {
    id: 'c4',
    title: '60-second guitar riff',
    brief: 'Original or cover. Vertical video, under a minute.',
    category: 'Music',
    creatorId: 'u4',
    creator: mockUsers.u4!,
    createdAt: hoursAgo(12),
    endsAt: hoursFromNow(72),
    submissionCount: 12,
    jemCount: 78,
    hasVoted: false,
  },
  {
    id: 'c5',
    title: 'Best 30-second a cappella cover',
    brief: 'No instruments, no autotune. Just your voice and a phone.',
    category: 'Singing',
    creatorId: 'u5',
    creator: mockUsers.u5!,
    createdAt: hoursAgo(96),
    endsAt: hoursFromNow(30),
    submissionCount: 134,
    jemCount: 892,
    hasVoted: false,
  },
];

/** Stable color tokens for cover gradients, keyed by challenge id. */
export const challengeCoverColors: Record<string, string> = {
  c1: 'from-orange-300 to-pink-400',
  c2: 'from-amber-300 to-red-400',
  c3: 'from-slate-400 to-slate-700',
  c4: 'from-indigo-400 to-purple-600',
  c5: 'from-rose-300 to-fuchsia-500',
};

/** Mock submissions keyed by challenge id. Each challenge has 3–4 submissions. */
export const mockSubmissionsByChallenge: Record<string, Submission[]> = {
  c1: [
    {
      id: 's1-1',
      challengeId: 'c1',
      userId: 'u6',
      user: mockUsers.u6!,
      mediaUrl: 'https://picsum.photos/800/800?random=1',
      mediaType: 'image',
      caption: 'Caught this beauty at Malibu this morning. Golden hour is unbeatable.',
      createdAt: hoursAgo(8),
      jemCount: 87,
      hasVoted: false,
      status: 'approved',
    },
    {
      id: 's1-2',
      challengeId: 'c1',
      userId: 'u7',
      user: mockUsers.u7!,
      mediaUrl: 'https://picsum.photos/800/800?random=2',
      mediaType: 'image',
      caption: 'Desert sunrise over the mountains. Worth waking up at 5am.',
      createdAt: hoursAgo(6),
      jemCount: 124,
      hasVoted: true,
      status: 'approved',
    },
    {
      id: 's1-3',
      challengeId: 'c1',
      userId: 'u8',
      user: mockUsers.u8!,
      mediaUrl: 'https://picsum.photos/800/800?random=3',
      mediaType: 'image',
      caption: 'Local beach, 6:30am. Peaceful start to the day.',
      createdAt: hoursAgo(2),
      jemCount: 101,
      hasVoted: false,
      status: 'flagged_by_user', // referenced as the flagged submission in mockNotifications below
    },
    {
      id: 's1-4',
      challengeId: 'c1',
      userId: 'u7',
      user: mockUsers.u7!,
      mediaUrl: 'https://picsum.photos/800/800?random=18',
      mediaType: 'image',
      caption: '[removed]',
      createdAt: hoursAgo(20),
      jemCount: 0,
      hasVoted: false,
      status: 'removed', // auto-removed by moderation — referenced by the submission_auto_removed notification (creator side) below
    },
  ],
  c2: [
    {
      id: 's2-1',
      challengeId: 'c2',
      userId: 'u6',
      user: mockUsers.u6!,
      mediaUrl: 'https://picsum.photos/800/800?random=4',
      mediaType: 'image',
      caption: 'Pasta with tomato, basil, olive oil, garlic, salt. Simple and delicious.',
      createdAt: hoursAgo(12),
      jemCount: 45,
      hasVoted: false,
      status: 'approved',
    },
    {
      id: 's2-2',
      challengeId: 'c2',
      userId: 'u7',
      user: mockUsers.u7!,
      mediaUrl: 'https://picsum.photos/800/800?random=5',
      mediaType: 'image',
      caption: 'Thai curry: coconut milk, curry paste, chicken, onions, lime juice.',
      createdAt: hoursAgo(10),
      jemCount: 67,
      hasVoted: false,
      status: 'approved',
    },
    {
      id: 's2-3',
      challengeId: 'c2',
      userId: 'u1',
      user: mockUsers.u1!,
      mediaUrl: 'https://picsum.photos/800/800?random=14',
      mediaType: 'image',
      caption: 'My take on the challenge.',
      createdAt: hoursAgo(28),
      jemCount: 3,
      hasVoted: false,
      status: 'removed', // referenced as violation #1 in mockNotifications below
    },
  ],
  c3: [
    {
      id: 's3-1',
      challengeId: 'c3',
      userId: 'u6',
      user: mockUsers.u6!,
      mediaUrl: 'https://picsum.photos/800/800?random=6',
      mediaType: 'image',
      caption: '1989 Honda Civic. Bone stock, runs like a dream. $4,200 total investment.',
      createdAt: hoursAgo(24),
      jemCount: 156,
      hasVoted: false,
      status: 'approved',
    },
    {
      id: 's3-2',
      challengeId: 'c3',
      userId: 'u7',
      user: mockUsers.u7!,
      mediaUrl: 'https://picsum.photos/800/800?random=7',
      mediaType: 'image',
      caption: 'Toyota Corolla, 1995. Paint is immaculate. Under $4k.',
      createdAt: hoursAgo(20),
      jemCount: 198,
      hasVoted: false,
      status: 'approved',
    },
    {
      id: 's3-3',
      challengeId: 'c3',
      userId: 'u8',
      user: mockUsers.u8!,
      mediaUrl: 'https://picsum.photos/800/800?random=8',
      mediaType: 'image',
      caption: 'Mazda Miata, just detailed. Best $3,500 I ever spent.',
      createdAt: hoursAgo(18),
      jemCount: 167,
      hasVoted: false,
      status: 'approved',
    },
    {
      id: 's3-4',
      challengeId: 'c3',
      userId: 'u1',
      user: mockUsers.u1!,
      mediaUrl: 'https://picsum.photos/800/800?random=16',
      mediaType: 'image',
      caption: 'My weekend project, finally done.',
      createdAt: hoursAgo(10),
      jemCount: 12,
      hasVoted: false,
      status: 'approved', // referenced in the challenge_ending_soon (participant) notification below
    },
  ],
  c4: [
    {
      id: 's4-1',
      challengeId: 'c4',
      userId: 'u6',
      user: mockUsers.u6!,
      mediaUrl: 'https://picsum.photos/800/800?random=9',
      mediaType: 'video',
      caption: 'Original riff I came up with last night. Fender Stratocaster.',
      createdAt: hoursAgo(16),
      jemCount: 34,
      hasVoted: false,
      status: 'approved',
    },
    {
      id: 's4-2',
      challengeId: 'c4',
      userId: 'u7',
      user: mockUsers.u7!,
      mediaUrl: 'https://picsum.photos/800/800?random=10',
      mediaType: 'video',
      caption: 'Hendrix cover. Tried to keep it authentic.',
      createdAt: hoursAgo(14),
      jemCount: 44,
      hasVoted: false,
      status: 'approved',
    },
    {
      id: 's4-3',
      challengeId: 'c4',
      userId: 'u1',
      user: mockUsers.u1!,
      mediaUrl: 'https://picsum.photos/800/800?random=15',
      mediaType: 'video',
      caption: 'My cover attempt.',
      createdAt: hoursAgo(3),
      jemCount: 1,
      hasVoted: false,
      status: 'removed', // referenced as violation #2 in mockNotifications below
    },
  ],
  c5: [
    {
      id: 's5-1',
      challengeId: 'c5',
      userId: 'u6',
      user: mockUsers.u6!,
      mediaUrl: 'https://picsum.photos/800/800?random=11',
      mediaType: 'video',
      caption: 'Billie Eilish - When We All Fall Asleep. Tried a whisper style.',
      createdAt: hoursAgo(4),
      jemCount: 201,
      hasVoted: false,
      status: 'approved',
    },
    {
      id: 's5-2',
      challengeId: 'c5',
      userId: 'u7',
      user: mockUsers.u7!,
      mediaUrl: 'https://picsum.photos/800/800?random=12',
      mediaType: 'video',
      caption: 'The Beatles - Yesterday. Acapella challenge accepted.',
      createdAt: hoursAgo(2),
      jemCount: 189,
      hasVoted: false,
      status: 'approved',
    },
    {
      id: 's5-3',
      challengeId: 'c5',
      userId: 'u8',
      user: mockUsers.u8!,
      mediaUrl: 'https://picsum.photos/800/800?random=13',
      mediaType: 'video',
      caption: 'Adele - Hello. First time posting, be gentle!',
      createdAt: hoursAgo(1),
      jemCount: 502,
      hasVoted: true,
      status: 'approved',
    },
    {
      id: 's5-4',
      challengeId: 'c5',
      userId: 'u1',
      user: mockUsers.u1!,
      mediaUrl: 'https://picsum.photos/800/800?random=17',
      mediaType: 'video',
      caption: 'Sam Smith - Stay With Me. First time trying falsetto.',
      createdAt: hoursAgo(5),
      jemCount: 180,
      hasVoted: false,
      status: 'approved', // referenced in the outjemmed notification below — s5-2 (189 jems) just passed this
    },
  ],
};

/**
 * Which mock user is "you" for local dev. u1 (Mina K.) created c1, so her
 * challenge is a natural source of creator-facing notifications
 * (new_submission, submission_flagged). challenge_ending_soon and outjemmed
 * fire for any challenge/submission the current user is following, whether
 * or not they created it.
 */
export const CURRENT_USER_ID = 'u1';

/**
 * Named test accounts for logging in as each of the app's three user
 * roles — admin, challenge creator, and submitter (end user) — without a
 * real backend. Enter the email at /login with any password (blank is
 * fine for now; see loginSchema's TODO in @jemmi/schemas) to log in as
 * that persona.
 *
 * Per ADMIN_EMAIL_DOMAIN in @jemmi/schemas, admin accounts are
 * restricted to @jemmi.fun emails — enforced structurally by
 * userSchema's refine(), not just as a convention here.
 */
export const mockTestAccounts = [
  {
    email: 'admin@jemmi.fun',
    userId: 'admin1',
    displayName: mockUsers.admin1!.displayName,
    roleLabel: 'Admin',
    isAdmin: true,
    isSuperAdmin: true, // test admin also has super-admin access, e.g. to LockedAccountsPage
  },
  {
    email: 'contentcreator@jemmi.fun',
    userId: 'u1', // Mina K. — already the creator of c1 in the rest of the mock data
    displayName: mockUsers.u1!.displayName,
    roleLabel: 'Challenge creator',
    isAdmin: false,
    isSuperAdmin: false,
  },
  {
    email: 'enduser@jemmi.fun',
    userId: 'u6', // Jamie L. — submits to challenges but never creates one
    displayName: mockUsers.u6!.displayName,
    roleLabel: 'Submitter',
    isAdmin: false,
    isSuperAdmin: false,
  },
] as const;

/**
 * Demo account for exercising the "account locked" login flow without a
 * real backend. In Phase 2, POST /api/auth/login checks users.isLocked
 * server-side and returns an 'account_locked' error with this same shape
 * (violationCount, lockedAt) instead of a generic invalid-credentials
 * error — see LoginPage's handlePasswordSubmit/handleOTPSubmit/
 * handleBiometricLogin for where that check plugs in.
 */
export const mockLockedOutAccount = {
  id: 'u9',
  displayName: 'Test User',
  email: 'locked@example.com',
  phone: '+15555550100',
  violationCount: 3,
  lockedAt: hoursAgo(2),
};

/** Shape shown on the super-admin locked-accounts page. */
export interface LockedAccountSummary {
  id: string;
  displayName: string;
  email: string;
  violationCount: number;
  lockedAt: string;
}

export const mockLockedAccounts: LockedAccountSummary[] = [
  {
    id: mockLockedOutAccount.id,
    displayName: mockLockedOutAccount.displayName,
    email: mockLockedOutAccount.email,
    violationCount: mockLockedOutAccount.violationCount,
    lockedAt: mockLockedOutAccount.lockedAt,
  },
  {
    id: 'u10',
    displayName: 'Chris Byrne',
    email: 'chris.byrne@example.com',
    violationCount: 3,
    lockedAt: hoursAgo(50),
  },
  {
    id: 'u11',
    displayName: 'Jordan Vega',
    email: 'j.vega@example.com',
    violationCount: 3,
    lockedAt: hoursAgo(216),
  },
];

const c1 = mockChallenges.find((c) => c.id === 'c1')!;
const c2 = mockChallenges.find((c) => c.id === 'c2')!;
const c3 = mockChallenges.find((c) => c.id === 'c3')!;
const c4 = mockChallenges.find((c) => c.id === 'c4')!;
const c5 = mockChallenges.find((c) => c.id === 'c5')!;
const flaggedSubmission = mockSubmissionsByChallenge.c1![2]!; // s1-3, by u8
const newSubmission = mockSubmissionsByChallenge.c1![1]!; // s1-2, by u7
const autoRemovedSubmission = mockSubmissionsByChallenge.c1![3]!; // s1-4, by u7 — for submission_auto_removed
const ownEntryInC3 = mockSubmissionsByChallenge.c3![3]!; // s3-4, by u1 — for the participant-role challenge_ending_soon
const ownEntryInC5 = mockSubmissionsByChallenge.c5![3]!; // s5-4, by u1 — for outjemmed

export const mockNotifications: NotificationDetail[] = [
  {
    id: 'n1',
    recipientId: CURRENT_USER_ID,
    type: 'submission_flagged',
    submissionId: flaggedSubmission.id,
    challengeId: c1.id,
    submission: flaggedSubmission,
    challenge: c1,
    message: `A submission in "${c1.title}" was flagged by the community.`,
    flagReportCount: 2,
    createdAt: hoursAgo(1),
    readAt: null,
    actionTaken: null,
    actionTakenAt: null,
  },
  {
    id: 'n2',
    recipientId: CURRENT_USER_ID,
    type: 'new_submission',
    submissionId: newSubmission.id,
    challengeId: c1.id,
    submission: newSubmission,
    challenge: c1,
    message: `${newSubmission.user.displayName} submitted an entry to "${c1.title}".`,
    flagReportCount: 0,
    createdAt: hoursAgo(3),
    readAt: null,
    actionTaken: null,
    actionTakenAt: null,
  },
  {
    id: 'n3',
    recipientId: CURRENT_USER_ID, // CREATOR role — u1 created c1
    type: 'challenge_ending_soon',
    submissionId: null,
    challengeId: c1.id,
    submission: null,
    challenge: c1,
    message: `Your challenge "${c1.title}" ends in a few hours — a good time to review any pending submissions before it closes.`,
    flagReportCount: 0,
    createdAt: hoursAgo(5),
    readAt: null,
    actionTaken: null,
    actionTakenAt: null,
  },
  {
    id: 'n4',
    recipientId: CURRENT_USER_ID, // SUBMITTER role — this must be u1's own submission
    type: 'outjemmed',
    submissionId: ownEntryInC5.id,
    challengeId: c5.id,
    submission: ownEntryInC5,
    challenge: c5,
    message: `Someone passed your entry in jems on "${c5.title}".`,
    flagReportCount: 0,
    createdAt: hoursAgo(9),
    readAt: hoursAgo(8),
    actionTaken: null,
    actionTakenAt: null,
  },
  {
    id: 'n5',
    recipientId: CURRENT_USER_ID,
    type: 'submission_flagged',
    submissionId: mockSubmissionsByChallenge.c1![0]!.id,
    challengeId: c1.id,
    submission: mockSubmissionsByChallenge.c1![0]!,
    challenge: c1,
    message: `A submission in "${c1.title}" was flagged by the community.`,
    flagReportCount: 1,
    createdAt: hoursAgo(30),
    readAt: hoursAgo(29),
    actionTaken: 'approved',
    actionTakenAt: hoursAgo(29),
  },
  {
    id: 'n6',
    recipientId: CURRENT_USER_ID, // addressed to the SUBMITTER, not the challenge creator
    type: 'submission_removed',
    submissionId: 's2-3',
    challengeId: c2.id,
    submission: mockSubmissionsByChallenge.c2![2]!, // s2-3
    challenge: c2,
    message: `Your submission to "${c2.title}" was removed for violating our content guidelines.`,
    flagReportCount: 0,
    violationCount: 1,
    createdAt: hoursAgo(26),
    readAt: hoursAgo(20),
    actionTaken: null,
    actionTakenAt: null,
  },
  {
    id: 'n7',
    recipientId: CURRENT_USER_ID,
    type: 'submission_removed',
    submissionId: 's4-3',
    challengeId: c4.id,
    submission: mockSubmissionsByChallenge.c4![2]!, // s4-3
    challenge: c4,
    message: `Your submission to "${c4.title}" was removed for violating our content guidelines.`,
    flagReportCount: 0,
    violationCount: 2,
    createdAt: hoursAgo(2),
    readAt: null,
    actionTaken: null,
    actionTakenAt: null,
  },
  {
    id: 'n8',
    recipientId: CURRENT_USER_ID, // SUBMITTER/PARTICIPANT role — u1 entered c3 but didn't create it
    type: 'challenge_ending_soon',
    submissionId: ownEntryInC3.id,
    challengeId: c3.id,
    submission: ownEntryInC3,
    challenge: c3,
    message: `A challenge you entered, "${c3.title}", ends in a few hours — last call to vote for your favorites.`,
    flagReportCount: 0,
    createdAt: hoursAgo(4),
    readAt: null,
    actionTaken: null,
    actionTakenAt: null,
  },
  {
    id: 'n9',
    recipientId: CURRENT_USER_ID, // CREATOR role — informational counterpart to a submitter's submission_removed
    type: 'submission_auto_removed',
    submissionId: autoRemovedSubmission.id,
    challengeId: c1.id,
    submission: null, // never attach media to this type — see the schema comment on submission_auto_removed
    challenge: c1,
    message: `A submission on your challenge "${c1.title}" was automatically removed by our content moderation system for violating our guidelines. No action is needed from you.`,
    flagReportCount: 0,
    createdAt: hoursAgo(20),
    readAt: null,
    actionTaken: null,
    actionTakenAt: null,
  },
  {
    id: 'n10',
    recipientId: 'u6', // enduser test persona — the only mock notification NOT addressed to CURRENT_USER_ID
    type: 'outjemmed',
    submissionId: mockSubmissionsByChallenge.c1![0]!.id, // s1-1, owned by u6, 87 jems
    challengeId: c1.id,
    submission: mockSubmissionsByChallenge.c1![0]!,
    challenge: c1,
    message: `Someone passed your entry in jems on "${c1.title}".`,
    flagReportCount: 0,
    createdAt: hoursAgo(6),
    readAt: null,
    actionTaken: null,
    actionTakenAt: null,
  },
];
